import { each } from "async";
import * as CONSTANTS from "../../../common/constants";
import { FSBLWindow } from "./localTypes";
import DockableWindow from "./Docking/dockableWindow";
import Logger from "../../../clients/logger";

enum groupStates {
	NORMAL = 0,
	MINIMIZING = 1,
	MINIMIZED = 2,
	RESTORING = 3,
}

// Typing the windows object
type FSBLWindows = Record<string, any>;

/**
 * A type which encapsulates information required to identify a window
 */
type WindowIdentifiable = {
	windowName?: string;
	name?: string;
};

export class WindowGroup {
	name: string | undefined;

	groupState: number = groupStates.NORMAL;

	GROUPSTATES = groupStates;

	// Convert to DockableWindow | FSBLWindow
	windows: Record<string, any> = {};

	interruptRestore: boolean = false;

	/**
	 *
	 */
	constructor(params: any) {
		this.name = params.name;
		if (params.windows) {
			this.windows = params.windows;
		}
	}

	destroy() {
		this.windows = {};
		this.name = undefined;
	}

	setWindows(windows: FSBLWindows) {
		this.windows = windows;
	}

	getWindows(): FSBLWindows {
		return this.windows;
	}

	addWindow(win: FSBLWindow | DockableWindow) {
		Logger.system.debug(`windowGroup.addWindow ${win.name} existing windows:`, Object.keys(this.windows));
		this.windows[win.name] = win;
	}

	/**
	 *
	 * @param arr either a window name or window identifier or a list of window names or identifiers
	 */
	removeWindows(arr: any | any[], cb = Function.prototype) {
		let windowName: string | undefined;
		if (!Array.isArray(arr)) {
			arr = [arr];
		}
		arr.forEach((win: any) => {
			if (typeof win === "string" || win instanceof String) {
				windowName = win.toString();
			} else {
				windowName = this.getWindowName(win);
			}
			Logger.system.debug("windowGroup.removeWindows", windowName);
			if (windowName) {
				delete this.windows[windowName];
			}
		}, this);
		cb();
	}

	/**
	 *
	 * @param win either a window name or window identifier
	 */
	getWindow(win?: string | WindowIdentifiable): any {
		if (!win) {
			return null;
		}

		//BoundWindow | null {
		if (typeof win === "string" || win instanceof String) {
			// we have a window name
			return this.windows[win.toString()];
		}

		const windowName = this.getWindowName(win);
		if (!!windowName) {
			// we have an identifier
			return this.windows[windowName];
		}

		return null;
	}

	// Typesafe version of getWindow - until such time as we separate FSBLWindow and DockableWindow
	// Returns null if the requested window is actually a FSBLWindow
	getDockableWindow(win?: string | WindowIdentifiable): DockableWindow | null {
		const theWindow = this.getWindow(win);
		if (theWindow?.win) return theWindow as DockableWindow;
		return null;
	}

	getWindowNames(): string[] {
		const names: string[] = [];
		for (const name in this.windows) {
			names.push(name);
		}
		return names;
	}

	addWindows(arr: any | any[]) {
		if (!Array.isArray(arr)) {
			arr = [arr];
		}
		arr.forEach((win: any) => {
			Logger.system.debug("windowGroup.addWindows", win.name);

			this.windows[win.name] = win;
		}, this);
	}

	getWindowArray(): any[] {
		const arr: any[] = [];
		for (const windowName in this.windows) {
			arr.push(this.windows[windowName]);
		}
		return arr;
	}

	minimizeAll() {
		if (this.groupState == groupStates.RESTORING) {
			this.interruptRestore = true;
			this.groupState = groupStates.NORMAL;
		}
		if (this.groupState !== groupStates.NORMAL) return;
		this.groupState = groupStates.MINIMIZING;
		for (const windowName in this.windows) {
			const win = this.windows[windowName];
			if (
				win.win?.windowState !== CONSTANTS.WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED &&
				win.win?.windowState !== CONSTANTS.WINDOWSTATE.MINIMIZED_WAS_NORMAL
			) {
				win.minimize();
			}
		}
		this.groupState = groupStates.MINIMIZED;
		this.interruptRestore = false;
	}

	minimize(params: any) {
		if (!params) {
			return this.minimizeAll();
		}
		let { windowList, componentType } = params;
		if (componentType) windowList = this.findAllByComponentType(componentType);

		for (const w of windowList) {
			const win = this.getWindowInstance(w);
			if (
				win.win?.windowState !== CONSTANTS.WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED &&
				win.win?.windowState !== CONSTANTS.WINDOWSTATE.MINIMIZED_WAS_NORMAL
			) {
				win.minimize();
			}
		}
	}

	restoreAll(cb: Function = Function.prototype) {
		if (this.groupState !== groupStates.MINIMIZED) return cb();
		this.groupState = groupStates.RESTORING;
		const restoreWindow = (windowName, done) => {
			if (this.interruptRestore) return done("restore interrupted");
			const win = this.windows[windowName];
			if (win.restore) {
				// if win.win exists, we're in a dockableGroup of dockalbeWindows
				const windowState = win.win ? win.win.windowState : win.windowState;
				if (windowState != CONSTANTS.WINDOWSTATE.NORMAL) {
					// The dockableWindow only takes a single parameter...a callback.
					if (win.win) {
						win.restore(done);
					} else {
						// All other window wraps accept 2 params for restore.
						win.restore({}, done);
					}
				} else {
					done();
				}
			} else {
				Logger.system.error(`${windowName} does not implement restore`);
				done();
			}
		};
		each(Object.keys(this.windows), restoreWindow, (err) => {
			this.interruptRestore = false;
			if (!err) {
				this.groupState = groupStates.NORMAL;
			}
			cb(err);
		});
	}

	// takes an array of window names.
	restore(params: any, cb: Function) {
		const { windowList } = params;
		const restoreWindow = (windowName: string, done: Function) => {
			const win = this.windows[windowName];
			if (!!win.restore) {
				this.windows[windowName].restore({}, done);
			} else {
				Logger.system.error(`${windowName} does not implement restore`);
				done();
			}
		};
		each(windowList, restoreWindow, cb as any);
	}

	/**
	 * Brings a group of windows to the front (BTF). In other words, puts those windows on top of any other windows so that they can be seen
	 *
	 * params: {
	 *   restoreWindows: boolean, // If true then windows will attempt to be restored (un-minimized) before being brought to front
	 *   windowList: any[],       // The list of windows to BTF. Defaults to the windows that are in this window group. This can be a
	 * 							  // list of window names, or a list of actual window instances.
	 *   componentType: any 	  // Optionally provide a componentType to BTF only those windows of that type in the list of windows.
	 *   sourceWindowName: string // Optional source (which window invoked this function call)
	 * }
	 *
	 * @param params window parameters
	 */
	bringToFront(
		params: { restoreWindows?: boolean; windowList?: any[]; componentType?: string; sourceWindowName?: string },
		cb: Function = Function.prototype
	) {
		if (!params) params = {};
		if (typeof params.restoreWindows === "undefined") params.restoreWindows = false;

		let { windowList, componentType } = params;
		// Determine if the windowList is a list of window names, or a list of actual windows (in which case we extract the name)
		if (windowList && typeof windowList[0] !== "string") {
			windowList = windowList.map((win) => win.windowName);
		}

		// Get all windows *in this group* of this component type, then convert them into an array of strings to be passed into the other group functions.
		if (componentType) {
			windowList = [];
			const windows = this.findAllByComponentType(componentType);
			windows.forEach((win) => {
				if (!!win?.name) {
					windowList?.push(win.name);
				}
			});
		}

		// Default to the windows in this group, actually the most common case
		let windowsToBTF = windowList ?? Object.keys(this.windows);

		// Sorts windows so that the maximized one is last in the list
		// KB52774 - bringToFront was not invoked on a group when a native window was maximized in the past; this happens now
		//           so the order in which the windows are brought to front is important.
		//
		// The sorted windows
		let sortedWindowsToBTF: string[] = [];
		// For each window to bring to front:
		windowsToBTF.forEach((windowName) => {
			// Get the window instance
			const w = this.getWindowInstance(windowName);
			// If the (window name is equal to the source window name) OR (the window state is maximized), then put at the END of the array
			if (
				(!!params.sourceWindowName && params.sourceWindowName === w?.win?.windowName) ||
				CONSTANTS.WINDOWSTATE.MAXIMIZED === w?.win?.windowState
			) {
				sortedWindowsToBTF.push(windowName);
			} else {
				// Otherwise, put at the front of the array
				sortedWindowsToBTF.unshift(windowName);
			}
		});

		// Assign windowsToBTF to be the sorted windows
		windowsToBTF = sortedWindowsToBTF;

		const doBTF = () => {
			each(
				windowsToBTF,
				(w: any, callback: Function = Function.prototype) => {
					const win = this.getWindowInstance(w);

					if (win) {
						win.bringToFront(callback);
					} else {
						callback();
					}
				},
				() => {
					cb();
				}
			);
		};

		if (params.restoreWindows) {
			if (typeof windowsToBTF[0] !== "string") {
				windowsToBTF = Object.keys(windowsToBTF);
			}
			this.restore({ windowsToBTF }, doBTF);
		} else {
			doBTF();
		}
	}

	hyperFocus(params: { windowList: any }) {
		const { windowList } = params;
		// If we got a list of identifiers, convert to names
		for (const w in windowList) {
			const win = windowList[w];
			if (!(typeof win === "string" || win instanceof String)) {
				windowList[w] = this.getWindowName(win);
			}
		}

		// If we are trying to hyper focus a stack make sure to also include the children
		for (const windowName in this.windows) {
			const win = this.getWindow(windowName);
			const parent = win?.getParent();
			// If window is in a stack and the stack is in the windowList but this window is not, add it.
			if (parent && windowList.includes(parent.name) && !windowList.includes(windowName)) {
				windowList.push(windowName);
			}
		}

		for (const windowName in this.windows) {
			if (!windowList.includes(windowName)) {
				this.windows[windowName].minimize();
			} else {
				this.windows[windowName].restore(() => {
					this.windows[windowName].bringToFront();
				});
			}
		}
	}

	findAllByComponentType(componentType: any) {
		const windowList: any[] = [];
		for (const windowName in this.windows) {
			const theWindow = this.windows[windowName];
			const descriptor = theWindow.windowDescriptor;
			if (componentType === (descriptor.component ? descriptor.component.type : descriptor.customData.component.type)) {
				windowList.push(this.windows[windowName]);
			}
		}
		return windowList;
	}

	/**
	 * Normalizes the way window instances may be obtained.
	 *
	 * @param win the object instance to use as the window identifier
	 * @returns the associated window instance
	 */
	getWindowInstance(win: WindowIdentifiable): any {
		if (!(typeof win === "string" || win instanceof String)) {
			return this.getWindow(this.getWindowName(win));
		} else {
			return this.getWindow(win);
		}
	}

	/**
	 * Gets the window name from a Windowish instance.
	 *
	 * @param win the window-like object to get the name from
	 * @returns the window name, or undefined if the name cannot be found
	 */
	getWindowName(win: WindowIdentifiable): string | undefined {
		return win?.windowName || win?.name;
	}
}
